import React from 'react'
import {
  faBatteryFull, faBolt, faHouse, faListCheck, faPlug,
} from '@fortawesome/free-solid-svg-icons'
import useDocumentTitle from '../hooks/useDocumentTitle'
import KreatorSzkielet from '../components/kreator/KreatorSzkielet'
import { dobierzMagazyn } from '../api/falowniki'

/**
 * Asystent doboru magazynu energii.
 *
 * ═══ CO TU JEST INNEGO ═══
 * Magazyn nie stoi sam: musi współpracować z falownikiem. Pierwsze pytanie
 * dotyczy więc falownika, a odpowiedź idzie do MODUŁU KOMPATYBILNOŚCI, a nie do
 * zgadywania po nazwie marki. Reguły pokrywają jedenaście marek; przy
 * pozostałych mówimy wprost, że zgodności nie zweryfikujemy.
 *
 * ═══ PYTANIE, NA KTÓRE JESZCZE NIE UMIEMY ODPOWIEDZIEĆ ═══
 * „Gdzie stanie" zadajemy, ale wynikiem jest OSTRZEŻENIE, nie filtr: kartę
 * katalogową ma 2 modele z 386, więc zakresu temperatur pracy nie znamy. Gdy
 * karty dojdą, ostrzeżenie zamieni się w filtr bez zmiany pytania.
 */

// Marki falowników, dla których mamy reguły kompatybilności. Lista jest krótka
// świadomie: to nie katalog marek, tylko te, przy których umiemy coś potwierdzić.
const MARKI_FALOWNIKOW = [
  'Deye', 'Huawei', 'SolarEdge', 'Fronius', 'GoodWe', 'Growatt',
  'Sofar', 'Solis', 'Sungrow', 'SMA', 'Hoymiles', 'FoxESS', 'Solax',
]

// Wiersze z danych badawczych o markach magazynów (`magazine_manufacturer_attribute`).
// Przy baterii najważniejsze pytanie nie brzmi „co potrafi", tylko „ile z niej
// zostanie po dziesięciu latach i co unieważnia gwarancję" — a tego nie ma
// w żadnej ofercie. Pokrycie: 365 modeli z 386.
const WIERSZE_BADAWCZE = [
  ['gwarancja_pojemnosc_po_latach', 'Gwarantowana pojemność'],
  ['gwarancja_warunki', 'Co unieważnia gwarancję'],
  ['certyfikaty_bezpieczenstwa', 'Certyfikaty'],
  ['serwis_pl_czy_jest', 'Serwis w Polsce'],
  ['aplikacja_monitoring', 'Aplikacja'],
]

// Wartości z badania to całe zdania — w komórce tabeli rozpychają wiersz na pół
// ekranu. Skracamy do pierwszego członu, a pełna treść zostaje w dymku pod ikoną
// źródła, więc nic nie ginie.
const pierwszyCzlon = (v, limit) => {
  const czysty = String(v).trim()
  const ciecie = czysty.search(/[;.](\s|$)/)
  const krotki = ciecie > 20 ? czysty.slice(0, ciecie) : czysty
  return krotki.length > limit ? `${krotki.slice(0, limit).trimEnd()}…` : krotki
}

const SKROTY = {
  gwarancja_pojemnosc_po_latach: (v) => pierwszyCzlon(v, 70),
  gwarancja_warunki: (v) => pierwszyCzlon(v, 90),
  certyfikaty_bezpieczenstwa: (v) => pierwszyCzlon(v, 60),
  serwis_pl_czy_jest: (v) => (/^\s*(brak|nie)\b/i.test(v) ? 'Nie' : 'Tak'),
  aplikacja_monitoring: (v) => pierwszyCzlon(v, 60),
}

const PYTANIA = [
  {
    klucz: 'marka_falownika', ikona: faPlug, etykieta: 'Falownik',
    tytul: 'Jaki masz albo planujesz falownik?',
    opis: 'Magazyn musi z nim współpracować. Sprawdzimy to w naszych regułach zgodności, zamiast zgadywać po nazwie marki.',
    typ: 'lista',
    // Znany model (Projekt 3D, krok Falownik) rozstrzyga zgodność sam, więc o markę i napięcie nie pytamy.
    warunek: (o) => !o.model_falownika,
    opcje: [
      ...MARKI_FALOWNIKOW.map((m) => ({ v: m, l: m })),
      { v: 'inna', l: 'Inna marka' },
      { v: 'nie_wiem', l: 'Jeszcze nie wiem' },
    ],
  },
  {
    // Marka nie wystarcza (błąd z produkcji 2026-10-26): GoodWe ma i serię niskonapięciową ES, i wysokonapięciową ET,
    // a falownik HV nie pracuje z baterią 48 V.
    klucz: 'napiecie_falownika', ikona: faPlug, etykieta: 'Napięcie baterii',
    tytul: 'Z jakimi bateriami pracuje falownik?',
    opis: 'Znajdziesz to w karcie katalogowej albo instrukcji falownika, w części o baterii. Falownik wysokonapięciowy nie pracuje z baterią 48 V i odwrotnie.',
    warunek: (o) => !o.model_falownika,
    opcje: [
      { v: 'HV', l: 'Wysokonapięciowy (HV)', o: 'Np. GoodWe ET, Huawei SUN2000, Deye SG01HP3' },
      { v: 'LV', l: 'Niskonapięciowy (LV, 48 V)', o: 'Np. GoodWe ES, Deye SG04LP3, Victron' },
      { v: 'nie_wiem', l: 'Nie wiem', o: 'Pokażemy oba rodzaje z oznaczeniem napięcia' },
    ],
  },
  {
    klucz: 'cel', ikona: faBolt, etykieta: 'Cel',
    tytul: 'Po co Ci magazyn?',
    // Taryfa i autokonsumpcja stoją razem, bo droga do obu jest ta sama:
    // przesunąć zużycie w czasie. Rozbicie ich na dwie odpowiedzi kazało
    // wybierać między celami, które osiąga się jednym i tym samym magazynem.
    opis: 'Od tego zależy, jakiej pojemności szukamy, i po czym oceniamy propozycje. Przy codziennym ładowaniu patrzymy też na limit cykli w gwarancji, bo to on wyczerpuje się wtedy pierwszy.',
    opcje: [
      { v: 'oszczedzanie', l: 'Oszczędzanie na taryfie / autokonsumpcja', o: 'Przesunięcie zużycia na tanie godziny' },
      { v: 'awarie', l: 'Zasilanie awaryjne', o: 'Zapas na czas bez prądu' },
      { v: 'oba', l: 'Jedno i drugie', o: 'Weźmiemy większe z dwóch wymagań' },
    ],
  },
  {
    klucz: 'dlugosc_awarii', ikona: faBolt, etykieta: 'Długość awarii',
    tytul: 'Jak długo trwają u Ciebie awarie?',
    // To pytanie zastąpiło błędne założenie, że zasilanie awaryjne zadowoli się
    // mniejszym magazynem. Zadowoli się mniejszym przy krótkich zanikach,
    // ale przy całodziennym braku prądu potrzebuje NAJWIĘCEJ ze wszystkich celów.
    opis: 'Pojemność liczymy z tego, ile godzin trzeba przetrwać bez sieci. Zakładamy przy tym ograniczony pobór: przy braku prądu nikt nie pierze ani nie ładuje auta.',
    warunek: (o) => o.cel === 'awarie' || o.cel === 'oba',
    opcjonalne: true,
    opcje: [
      { v: 'krotkie', l: 'Krótkie, do godziny', o: 'Wystarczy niewielki zapas' },
      { v: 'kilka_godzin', l: 'Kilka godzin', o: 'Średni zapas' },
      { v: 'dlugie', l: 'Cały dzień albo dłużej', o: 'Potrzebny duży zapas' },
    ],
  },
  {
    klucz: 'zuzycie', ikona: faBatteryFull, etykieta: 'Zużycie',
    tytul: 'Ile prądu zużywasz?',
    // Dwa osobne pytania, z których drugie pokazywało się tylko po pominięciu
    // pierwszego, wyglądały jak kara za nieznajomość własnego zużycia. Jedno
    // pytanie z przełącznikiem mówi wprost: podaj to, co masz pod ręką.
    opis: 'Roczne zużycie znajdziesz na rozliczeniu rocznym. Jeśli go nie masz, przełącz na rachunek miesięczny, a my przeliczymy go po średniej cenie razem z dystrybucją.',
    typ: 'liczba', opcjonalne: true,
    jednostki: [
      { v: 'kwh', l: 'Rocznie w kWh', jednostka: 'kWh', placeholder: 'np. 5000' },
      { v: 'zl', l: 'Rachunek w zł', jednostka: 'zł/mies.', placeholder: 'np. 400' },
    ],
    liczba: { jednostka: 'kWh', min: 50, krok: 50 },
  },
  {
    klucz: 'miejsce', ikona: faHouse, etykieta: 'Miejsce',
    tytul: 'Gdzie stanie magazyn?',
    opis: 'Baterie źle znoszą mróz. Dziś umiemy Cię tylko ostrzec, bo dane o zakresie temperatur mamy dla nielicznych modeli.',
    opcje: [
      { v: 'wnetrze', l: 'W ogrzewanym wnętrzu', o: 'Bez zastrzeżeń' },
      { v: 'garaz_nieogrzewany', l: 'W nieogrzewanym garażu', o: 'Ostrzeżemy o temperaturze' },
      { v: 'nie_wiem', l: 'Jeszcze nie wiem', o: '' },
    ],
  },
  {
    klucz: 'priorytet', ikona: faListCheck, etykieta: 'Priorytet',
    tytul: 'Co jest dla Ciebie ważniejsze?', opis: '',
    opcje: [
      { v: 'ekonomia', l: 'Najniższa cena za kWh', o: 'Cena waży najmocniej' },
      { v: 'balans', l: 'Rozsądny balans', o: 'Cena i dopasowanie po równo' },
      { v: 'jakosc', l: 'Dopasowanie do potrzeb', o: 'Cena schodzi na dalszy plan' },
    ],
  },
]

/**
 * Zużycie przychodzi jako para liczba plus jednostka, a backend chce dwóch
 * osobnych pól, bo z rachunku liczy kilowatogodziny po własnym przeliczniku.
 */
const doKryteriow = (o) => {
  const { zuzycie, ...reszta } = o
  const wKwh = !zuzycie?.jednostka || zuzycie.jednostka === 'kwh'
  return {
    priorytet: 'balans',
    ...reszta,
    zuzycie_kwh_rok: zuzycie?.ile && wKwh ? zuzycie.ile : null,
    rachunek_mies_pln: zuzycie?.ile && !wKwh ? zuzycie.ile : null,
  }
}

// `osadzony`: strona w oknie nad Projektem 3D (uwaga F7, 2026-10-25): odpowiedzi startowe z modelu, bez zmiany tytułu
// karty, a „Użyj w projekcie” przy propozycji oddaje ją do `onUzyjModelu`.
// Marka falownika z Projektu 3D spoza listy pytania to „Inna marka”, żeby pytanie nie zostało z wartością bez opcji.
const startZModelu = (o) => {
  if (!o) return {}
  const { marka_falownika: marka, ...reszta } = o
  if (!marka) return reszta
  return { ...reszta, marka_falownika: MARKI_FALOWNIKOW.includes(marka) ? marka : 'inna' }
}

const DoborMagazynu = ({ osadzony = false, odpowiedziStartowe = null, onUzyjModelu = null }) => {
  useDocumentTitle(osadzony ? 'Projekt 3D' : 'Asystent doboru magazynu energii')
  return (
    <KreatorSzkielet
      tytul="Asystent doboru magazynu energii"
      opis="Odpowiedz na kilka pytań o swoją instalację i zużycie. Policzymy potrzebną pojemność i pokażemy trzy propozycje z uzasadnieniem."
      pytania={PYTANIA}
      domyslne={osadzony ? startZModelu(odpowiedziStartowe) : {}}
      tekstPrzycisku="Pokaż dopasowane magazyny"
      onDobierz={(o) => dobierzMagazyn(doKryteriow(o))}
      konfiguracjaWynikow={(wynik) => ({
        kategoria: 'magazyn',
        wiersze: [
          ['Pojemność', (p) => (p.pojemnosc_kwh ? `${String(p.pojemnosc_kwh).replace('.', ',')} kWh` : null)],
          ['Cena za kWh', (p) => `${p.cena_za_kwh} zł/kWh`],
          ['Napięcie', (p) => p.napiecie],
          ['Rodzaj', (p) => p.typ],
          ['Liczba ofert', (p) => `${p.ofert}`],
        ],
        wierszeBadawcze: WIERSZE_BADAWCZE,
        skroty: SKROTY,
        cta: (p) => `Zobacz ${p.ofert} ofert`,
        ...(onUzyjModelu ? { onUzyj: onUzyjModelu } : {}),
        stopka: [
          wynik.zalecana_pojemnosc_kwh
            ? `Policzona pojemność to ${String(wynik.zalecana_pojemnosc_kwh).replace('.', ',')} kWh, wyliczona z Twojego zużycia i celu.`
            : 'Bez podanego zużycia nie liczymy zalecanej pojemności.',
          wynik.zgodnosc === 'model'
            ? `Pokazujemy tylko magazyny zgodne z falownikiem ${wynik.falownik || ''} według naszych reguł kompatybilności.`
            : wynik.zgodnosc_sprawdzona
              ? 'Zgodność z marką falownika sprawdzona w naszych regułach kompatybilności.'
              : 'Zgodności z falownikiem nie sprawdziliśmy, bo nie znamy jego marki albo nie mamy dla niej reguł.',
          // Bez modelu i bez napięcia lista może łączyć baterie HV i LV (napięcie stoi przy każdej propozycji).
          wynik.napiecie_do_potwierdzenia
            ? 'Nie znamy napięcia baterii Twojego falownika, więc zgodność trzeba potwierdzić przed zakupem: magazyn wysokonapięciowy nie zadziała z falownikiem niskonapięciowym i odwrotnie.'
            : null,
          'Nie znamy zakresu temperatur pracy, więc przy montażu w chłodnym miejscu potwierdź go u sprzedawcy. Warunki gwarancji pochodzą z naszego badania marek, a nie z oferty sklepu; ikona informacji przy wartości prowadzi do źródła.',
        ].filter(Boolean).join(' '),
      })}
    />
  )
}

export default DoborMagazynu
